"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  Calendar,
  Clock,
  ArrowLeft,
  CheckCircle2,
  ShoppingBag,
  ArrowRight,
  Tag,
  User,
} from "lucide-react";

// হোমপেজের ৩টি ব্লগের হুবহু ডাটাবেজ (ফলব্যাক)
const BLOG_DATABASE: Record<string, any> = {
  "10-healthy-vegetables-for-daily-diet": {
    title: "১০টি স্বাস্থ্যকর সবজি যা প্রতিদিনের খাবারে রাখা উচিত",
    category: "Healthy Eating",
    published_at: "2026-09-17",
    read_time: "৫ মিনিট",
    author_name: "Efaz Admin",
    author_role: "Store Admin",
    image: "https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?auto=format&fit=crop&w=1200&q=80",
    summary:
      "প্রতিদিনের খাদ্যতালিকায় তাজা ও পুষ্টিকর সবজি রাখা শরীরের রোগ প্রতিরোধ ক্ষমতা বাড়াতে অত্যন্ত জরুরি। গাজর, পালং শাকসহ পুষ্টিগুণে ভরপুর সেরা ১০টি সবজির তালিকা নিচে আলোচনা করা হলো।",
    content: [
      {
        heading: "১. গাজর (ভিটামিন-এ ও বিটা ক্যারোটিনের উৎস)",
        text: "গাজর দৃষ্টিশক্তি ভালো রাখতে এবং ত্বক উজ্জ্বল রাখতে সহায়তা করে। এতে থাকা অ্যান্টিঅক্সিডেন্ট শরীরের কোষগুলোকে সুরক্ষা প্রদান করে।",
      },
      {
        heading: "২. পালং শাক (আয়রন ও ফলিক অ্যাসিড)",
        text: "পালং শাকে প্রচুর পরিমাণে আয়রন ও ভিটামিন রয়েছে, যা রক্তস্বল্পতা দূর করে এবং শরীরের ক্লান্তিভাব কমাতে সাহায্য করে।",
      },
      {
        heading: "৩. ব্রকলি ও ফুলকপি",
        text: "এতে প্রচুর ডায়েটারি ফাইবার ও ভিটামিন সি থাকে, যা হজম শক্তি বাড়ায় এবং হৃদরোগের ঝুঁকি হ্রাস করে।",
      },
      {
        heading: "৪. টমেটো ও শসা",
        text: "টমেটোর লাইকোপেন হার্ট ভালো রাখে এবং শসা শরীরে পানির ভারসাম্য বজায় রাখতে চমৎকার কাজ করে।",
      },
    ],
    tags: ["স্বাস্থ্যকর খাবার", "তাজা সবজি", "অর্গানিক", "পুষ্টি"],
  },
  "how-to-create-a-grocery-shopping-list-stay-organized": {
    title: "How to Create a Grocery Shopping List: Stay Organized...",
    category: "Shopping Tips",
    published_at: "14 August 2024",
    read_time: "4 min read",
    author_name: "Jenny Alexander",
    author_role: "Shopping Specialist",
    image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80",
    summary:
      "Planning your grocery shopping ahead saves money, cuts food waste, and keeps your meal preparations on schedule throughout the week.",
    content: [
      {
        heading: "1. Take Inventory Before You Shop",
        text: "Always check your refrigerator and pantry first. Note down items that are completely finished to avoid unnecessary double purchases.",
      },
      {
        heading: "2. Categorize Your Shopping List",
        text: "Group items into Fresh Produce, Dairy, Grains, and Household Essentials so you can navigate aisles smoothly.",
      },
    ],
    tags: ["Shopping Tips", "Budgeting", "Grocery Hacks"],
  },
  "summer-produce-guide-fresh-fruits-and-vegetables-to-enjoy": {
    title: "Summer Produce Guide: Fresh Fruits and Vegetables to Enjoy",
    category: "Seasonal Guides",
    published_at: "13 August 2024",
    read_time: "6 min read",
    author_name: "Jenny Alexander",
    author_role: "Seasonal Food Guide",
    image: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?auto=format&fit=crop&w=1200&q=80",
    summary:
      "Summer brings an abundance of hydrating, vitamin-rich fruits and crisp vegetables. Here is your ultimate seasonal shopping list.",
    content: [
      {
        heading: "1. Hydrating Melons & Pineapples",
        text: "Watermelons, cantaloupes, and pineapples keep electrolytes balanced during hot sunny afternoons.",
      },
      {
        heading: "2. Crisp Cucumbers & Summer Gourds",
        text: "Include gourds and crisp greens in your midday meals to promote easy digestion and body temperature regulation.",
      },
    ],
    tags: ["Seasonal Guides", "Summer Produce", "Healthy Living"],
  },
};

export default function BlogPostDetailPage() {
  const params = useParams();
  const subdomain = (params?.subdomain as string) || "xyzstore";
  const rawSlug = (params?.slug as string) || "";
  const slug = decodeURIComponent(rawSlug).trim().toLowerCase();

  const [post, setPost] = useState<any>(null);

  // লোকাল ও লাইভ ডোমেইনের জন্য ডায়নামিক API Base URL
  const apiBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000`;
      }
      return ""; // লাইভ ডোমেইনে Caddy প্রক্সির জন্য রিলেটিভ পাথ
    }
    return `http://${subdomain}.localhost:8000`;
  }, [subdomain]);

  // ইমেজ URL সঠিকভাবে প্রসেস করার ফাংশন
  const resolveImageUrl = (imgUrl?: string) => {
    if (!imgUrl) return "";
    if (imgUrl.startsWith("http://") || imgUrl.startsWith("https://")) {
      return imgUrl;
    }
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000${imgUrl.startsWith("/") ? "" : "/"}${imgUrl}`;
      }
      return imgUrl.startsWith("/") ? imgUrl : `/${imgUrl}`;
    }
    return `http://${subdomain}.localhost:8000${imgUrl.startsWith("/") ? "" : "/"}${imgUrl}`;
  };

  useEffect(() => {
    async function fetchBlogPost() {
      // ১. ব্যাকএন্ড এপিআই থেকে ফেচ করা
      try {
        const res = await fetch(`${apiBase}/api/v1/stores/blogs/${slug}/`);
        if (res.ok) {
          const data = await res.json();
          setPost({
            title: data.title,
            category: data.category_tag || "Healthy Eating",
            published_at: data.published_at,
            read_time: "৫ মিনিট",
            author_name: data.author_name || "Efaz Admin",
            author_role: "Store Admin",
            image: resolveImageUrl(data.cover_image) || "https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?auto=format&fit=crop&w=1200&q=80",
            summary: data.summary,
            content: Array.isArray(data.content)
              ? data.content
              : [{ heading: "বিস্তারিত বিবরণ", text: data.content }],
            tags: ["স্বাস্থ্যকর খাবার", "অর্গানিক", "তাজা সবজি"],
          });
          return;
        }
      } catch {
        // ব্যাকএন্ড রেসপন্স না করলে নিচের ফলব্যাক ডাটা ব্যবহার হবে
      }

      // ২. লোকাল ডাটাবেজ থেকে কার্ড অনুযায়ী তথ্য লোড করা
      if (BLOG_DATABASE[slug]) {
        setPost(BLOG_DATABASE[slug]);
      } else {
        const formattedTitle = slug
          .split("-")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        setPost({
          title: formattedTitle || "১০টি স্বাস্থ্যকর সবজি যা প্রতিদিনের খাবারে রাখা উচিত",
          category: "Healthy Eating",
          published_at: "2026-09-17",
          read_time: "৫ মিনিট",
          author_name: "Efaz Admin",
          author_role: "Store Admin",
          image: "https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?auto=format&fit=crop&w=1200&q=80",
          summary: "প্রতিদিনের খাদ্যতালিকায় পুষ্টিকর সবজি রাখা অত্যন্ত জরুরি।",
          content: [
            {
              heading: "স্বাস্থ্যকর সবজির উপকারিতা",
              text: "নিয়মিত শাকসবজি খেলে শরীরে ভিটামিন ও মিনারেলের ঘাটতি পূরণ হয়।",
            },
          ],
          tags: ["স্বাস্থ্যকর খাবার", "অর্গানিক"],
        });
      }
    }

    if (slug) {
      fetchBlogPost();
    }
  }, [apiBase, subdomain, slug]);

  const article = post || BLOG_DATABASE["10-healthy-vegetables-for-daily-diet"];

  return (
    <article className="min-h-screen bg-slate-50/50 pb-20">
      {/* টপ হেডার */}
      <div className="bg-white border-b border-slate-100 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#059669] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Store
          </Link>

          <div className="space-y-3">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-[#059669] text-xs font-black tracking-wide uppercase">
              {article.category}
            </span>

            <h1 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight tracking-tight">
              {article.title}
            </h1>

            {/* মেটা ইনফরমেশন */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-100 text-xs text-slate-500">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-[#059669] flex items-center justify-center font-bold">
                  <User className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-bold text-slate-800 block">{article.author_name}</span>
                  <span className="text-[11px] text-slate-400">{article.author_role}</span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {article.published_at}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  {article.read_time}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* আর্টিকেল বডি */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-8">
        <div className="relative w-full h-72 sm:h-96 rounded-3xl overflow-hidden shadow-sm mb-8 border border-slate-100 bg-emerald-50">
          <Image
            src={article.image}
            alt={article.title}
            fill
            unoptimized
            priority
            className="object-cover"
          />
        </div>

        {/* সামারি কার্ড */}
        <div className="p-6 rounded-2xl bg-emerald-50/70 border border-emerald-100 text-slate-700 text-sm font-medium leading-relaxed mb-8">
          <p className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
            <span>{article.summary}</span>
          </p>
        </div>

        {/* কনটেন্ট সেকশন */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10 space-y-8">
          {article.content.map((section: any, idx: number) => (
            <div key={idx} className="space-y-2">
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                {section.heading}
              </h2>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                {section.text}
              </p>
            </div>
          ))}

          {/* ট্যাগস */}
          <div className="pt-6 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <Tag className="w-3.5 h-3.5 text-slate-400" />
            {article.tags.map((t: string) => (
              <span
                key={t}
                className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold"
              >
                #{t}
              </span>
            ))}
          </div>
        </div>

        {/* শপ ব্যানার */}
        <div className="mt-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-lg shadow-emerald-600/10">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg font-black tracking-tight">তাজা ও অর্গানিক শাকসবজি চান?</h3>
            <p className="text-xs text-emerald-100">
              আমাদের স্টোর থেকে সরাসরি ফার্ম-ফ্রেশ পণ্য অর্ডার করুন এবং দ্রুত ডেলিভারি নিন।
            </p>
          </div>

          <Link
            href="/products"
            className="px-6 py-3 rounded-full bg-white text-[#059669] hover:bg-emerald-50 text-xs font-black transition-all flex items-center gap-2 shadow-sm flex-shrink-0"
          >
            <ShoppingBag className="w-4 h-4" />
            <span>Shop Fresh Veggies</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}